/**
 * OnboardingTour — US-07-04 Production
 *
 * ┌─── LABEL / STATE SYSTEM ────────────────────────────────────────────────┐
 * │ Supabase source of truth (migration: scripts/user-preferences-onboarding.sql):│
 * │   profiles.preferred_industry       — Legal/Finance/Audit/Export/RE/Other │
 * │   profiles.preferred_source_lang    — ISO 639-1 (vi/en/ja/...)           │
 * │   profiles.preferred_target_lang    — ISO 639-1                          │
 * │   profiles.onboarding_completed_at  — TIMESTAMPTZ                        │
 * │   profiles.onboarding_last_step     — INT (funnel analytics)             │
 * │                                                                          │
 * │ localStorage cache (instant read):                                       │
 * │   tour_industry, tour_source_lang, tour_target_lang,                     │
 * │   tour_completed, tour_last_step                                         │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Flow:
 *   1. User pass Folder Gate (US-07-01) → enabled=true
 *   2. OnboardingTour hydrate Supabase → check completed flag
 *   3. Nếu chưa completed → auto show WelcomeModal sau 800ms
 *   4. User qua 3 steps (industry + source lang + target lang)
 *   5. Confirm → save Supabase + localStorage → driver.js tour 7 bước
 *   6. Tour done/skip → mark onboarding_completed_at
 *   7. Help button (?) floating luôn available → force re-open
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour-driver-overrides.css';
import WelcomeModal, { type OnboardingChoices } from './IndustryPickerModal';
import { buildTourSteps } from './tour-steps';
import { useUserPreferences } from '../../../hooks/useUserPreferences';
import { usePrivacyStore } from '../../../store/usePrivacyStore';

interface Props {
  userName?: string;
  enabled?: boolean;
}

const OnboardingTour: React.FC<Props> = ({ userName, enabled = true }) => {
  const [showWelcome, setShowWelcome] = useState(false);
  const driverRef = useRef<Driver | null>(null);
  const { prefs, isLoading, markTourComplete, updateLastStep } = useUserPreferences();
  const setPrivacyMode = usePrivacyStore(s => s.setMode);

  // Auto-show welcome lần đầu (sau folder gate, sau Supabase hydrate)
  useEffect(() => {
    if (!enabled || isLoading) return;
    if (prefs.onboarding_completed_at) return; // đã complete
    const t = setTimeout(() => setShowWelcome(true), 800);
    return () => clearTimeout(t);
  }, [enabled, isLoading, prefs.onboarding_completed_at]);

  const startDriverTour = useCallback((industry: string) => {
    driverRef.current = driver({
      showProgress: true,
      progressText: 'Bước {{current}} / {{total}}',
      nextBtnText: 'Tiếp',
      prevBtnText: 'Trước',
      doneBtnText: 'Xong',
      allowClose: true,
      disableActiveInteraction: false,
      onHighlightStarted: (_el, step, opts) => {
        // Track funnel: update last step reached
        updateLastStep(opts.state.activeIndex !== undefined ? opts.state.activeIndex + 1 : 0);
      },
      onDestroyed: () => {
        // Tour done — mark complete (if not already done in step 3)
      },
      steps: buildTourSteps(industry),
    });
    driverRef.current.drive();
  }, [updateLastStep]);

  const handleStart = useCallback(async (choices: OnboardingChoices) => {
    setShowWelcome(false);
    // Save privacy mode (Zustand persist) + preferences (Supabase)
    setPrivacyMode(choices.privacyMode);
    await markTourComplete(choices);
    // Small delay để modal unmount trước driver takeover
    setTimeout(() => startDriverTour(choices.industry), 250);
  }, [markTourComplete, startDriverTour, setPrivacyMode]);

  const handleSkip = useCallback(() => {
    setShowWelcome(false);
    // Mark completed với defaults (user không pick → cloud_sync + Other + vi/en)
    markTourComplete({
      industry: 'Other',
      sourceLang: prefs.preferred_source_lang || 'vi',
      targetLang: prefs.preferred_target_lang || 'en',
    });
  }, [markTourComplete, prefs.preferred_source_lang, prefs.preferred_target_lang]);

  const handleHelpClick = useCallback(() => {
    // User click "?" → start driver.js tour TRỰC TIẾP với industry đã lưu (skip welcome modal)
    if (driverRef.current) {
      driverRef.current.destroy();
      driverRef.current = null;
    }
    const savedIndustry = prefs.preferred_industry || 'Other';
    startDriverTour(savedIndustry);
  }, [prefs.preferred_industry, startDriverTour]);

  // Listen global event 'tour:replay' (dispatched từ Sidebar user menu)
  useEffect(() => {
    window.addEventListener('tour:replay', handleHelpClick);
    return () => window.removeEventListener('tour:replay', handleHelpClick);
  }, [handleHelpClick]);

  return (
    <WelcomeModal
      isOpen={showWelcome}
      userName={userName}
      onStart={handleStart}
      onSkip={handleSkip}
    />
  );
};

export default OnboardingTour;
